import {
  Badge,
  Box,
  Collapse,
  Title,
  Group,
  Divider,
  Stack,
  Text,
  Paper,
  Center,
  Flex,
  useMantineTheme,
} from "@mantine/core";
import { LuChevronRight } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { StyledMarkdown } from "@/ui/components/StyledMarkdown";
import { useDisclosure } from "@mantine/hooks";
import {
  leftAccentCardProps,
  surfaceCardProps,
  surfaceInsetPaperProps,
  surfaceRankBadgeProps,
} from "@canucci/base-mantine/theme";

interface InsightsDetailsProps {
  topic: {
    target_id: string;
    groupTitle: string;
    topicTitle: string;
    topicSummary?: string;
    vote_count: number;
    comments: string[];
  } | null;
  allTopics: Array<{ target_id: string }>;
  isMobile: boolean | undefined;
}

function InsightsDetails({ topic, allTopics, isMobile }: InsightsDetailsProps) {
  const { t: tPages } = useTranslation("pages");
  const theme = useMantineTheme();
  const [isOpen, handlers] = useDisclosure(true);

  if (!topic) {
    if (isMobile ?? false) {
      return null;
    }
    return (
      <Paper {...surfaceCardProps} p="xl" mih="200px">
        <Center h="100%">
          <Text c="var(--text-on-dark-muted)" ta="center">
            {tPages("canvas.insights.selectPriority")}
          </Text>
        </Center>
      </Paper>
    );
  }

  const topicIndex = allTopics.findIndex((t) => t.target_id === topic.target_id) + 1;

  const content = (
    <Stack align="stretch" gap="md">
      <Box pb="md">
        <Group gap="xs" mb="sm">
          <Badge {...surfaceRankBadgeProps()} size="sm">
            #{topicIndex}
          </Badge>
          <Text fz="sm" c="var(--text-on-dark-muted)" fw={500}>
            {topic.groupTitle}
          </Text>
        </Group>
        {!isMobile && (
          <Title size="h3" mb="sm" c="var(--text-on-dark)">
            {topic.topicTitle}
          </Title>
        )}
        <Text fz="md" c="var(--text-on-dark)" fw={500}>
          {tPages("canvas.insights.peopleHighlighted", {
            count: topic.vote_count,
          })}
        </Text>
      </Box>

      {!isMobile && <Divider color="var(--surface-inset)" />}

      {topic.topicSummary && (
        <Box>
          <Flex
            onClick={() => handlers.toggle()}
            align="center"
            gap="xs"
            py="xs"
            w="100%"
            style={{
              cursor: "pointer",
            }}
          >
            <LuChevronRight
              size={16}
              color="var(--text-on-dark)"
              style={{
                transform: isOpen ? "rotate(90deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
            <Text fz="sm" fw={500} c="var(--text-on-dark)">
              {tPages("canvas.insights.aboutThisFocusArea")}
            </Text>
          </Flex>
          <Collapse in={isOpen}>
            <Paper {...surfaceInsetPaperProps} mt="xs" p="md" fz="sm" c="var(--text-on-dark)">
              <StyledMarkdown content={topic.topicSummary || ""} paragraphTextProps={{ c: "var(--text-on-dark)" }} />
            </Paper>
          </Collapse>
        </Box>
      )}

      {topic.comments.length > 0 && (
        <>
          {topic.topicSummary && <Divider color="var(--surface-inset)" />}
          <Box>
            <Text fz="sm" fw={500} mb="sm" c="var(--text-on-dark)">
              {tPages("canvas.insights.comments")} ({topic.comments.length})
            </Text>
            <Stack gap="xs">
              {topic.comments.map((comment, commentIdx) => (
                <Paper key={commentIdx} {...surfaceInsetPaperProps} p="sm">
                  <Text fz="sm" c="var(--text-on-dark)">
                    {comment}
                  </Text>
                </Paper>
              ))}
            </Stack>
          </Box>
        </>
      )}

      {topic.comments.length === 0 && (
        <>
          {topic.topicSummary && <Divider color="var(--surface-inset)" />}
          <Text fz="sm" c="var(--text-on-dark-muted)" fs="italic">
            {tPages("canvas.insights.noComments")}
          </Text>
        </>
      )}
    </Stack>
  );

  if (isMobile) {
    return (
      <Box id="insights-details-pane" p={0} pos="relative">
        {content}
      </Box>
    );
  }

  return (
    <Box
      id="insights-details-pane"
      pos="sticky"
      top={theme.spacing.xl}
      mah="calc(100vh - 12rem)"
      style={{ overflowY: "auto" }}
    >
      <Paper {...leftAccentCardProps} p="xl">
        {content}
      </Paper>
    </Box>
  );
}

export default InsightsDetails;
